import { createContext } from 'react';
import type { ReactNode } from 'react';
import {
  User,
  UserRole,
  Product,
  Order,
  OrderStatus,
  Invoice,
  Testimonial,
  Notification,
  NewProductData,
  NewOrderData,
  NewTestimonialData,
  NewCustomerData,
  NotificationType,
  Inquiry,
  NewInquiryData,
  Category,
  InventoryItem,
  InventoryUsageLog,
  OrderEvent,
  Shipment,
  PaymentTransaction,
  CustomerBranch,
  NewCustomerBranchData,
  ThemeMode,
  ResolvedTheme
} from '../types';

export interface AppContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  products: Product[];
  categories: Category[];
  orders: Order[];
  invoices: Invoice[];
  customers: User[];
  testimonials: Testimonial[];
  notifications: Notification[];
  customerInbox: Notification[];
  adminInbox: Notification[];
  inquiries: Inquiry[];
  siteContent: Record<string, any>;
  orderEvents: OrderEvent[];
  shipments: Shipment[];
  inventoryItems: InventoryItem[];
  inventoryUsageLogs: InventoryUsageLog[];
  branches: CustomerBranch[];
  paymentTransactions: PaymentTransaction[];
  themeMode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  login: (email: string, password: string) => Promise<User | null>;
  signup: (data: NewCustomerData) => Promise<boolean>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<boolean>;
  switchUser: (id: string | number, role: UserRole) => Promise<void>;
  addCategory: (name: string, parentId: number | null) => Promise<void>;
  updateCategory: (id: number, name: string, parentId: number | null) => Promise<void>;
  deleteCategory: (id: number) => Promise<void>;
  addProduct: (data: NewProductData) => Promise<void>;
  updateProduct: (id: number, data: Partial<NewProductData>) => Promise<void>;
  deleteProduct: (id: number) => Promise<void>;
  createOrder: (data: NewOrderData) => Promise<void>;
  amendDeniedOrder: (orderId: number, data: NewOrderData) => Promise<void>;
  updateOrderStatus: (id: number, status: OrderStatus, prices?: Record<string, number>) => Promise<void>;
  reviewCustomerOrder: (orderId: number, approved: boolean, remark?: string) => Promise<void>;
  shipOrder: (orderId: number, trackingNumber: string, courierPartner?: string) => Promise<void>;
  markShipmentDelivered: (orderId: number) => Promise<void>;
  setOrderItemPacked: (orderId: number, itemId: number, packed: boolean) => Promise<void>;
  submitOrderReceiptChecks: (orderId: number, checks: Array<{ itemId?: number; receivedQuantity: number; remark?: string }>) => Promise<void>;
  syncInventoryFromReceipt: (
    orderId: number,
    checks: Array<{ itemId?: number; receivedQuantity: number; remark?: string }>
  ) => Promise<void>;
  updateInventoryThreshold: (branchId: number | null | undefined, productId: number, variationId: string | undefined, threshold: number) => Promise<void>;
  issueInventoryStock: (inventoryItemId: number | string, quantity: number, note?: string) => Promise<void>;
  markInvoiceAsPaid: (id: number) => Promise<void>;
  processInvoicePayment: (invoiceId: number, provider?: string) => Promise<void>;
  requestInvoiceRefund: (invoiceId: number, amount?: number, notes?: string) => Promise<void>;
  downloadPaymentReceipt: (invoiceId: number, transactionId?: number) => void;
  reconcileInvoice: (invoiceId: number) => Promise<void>;
  updateInvoice: (id: number, updates: Partial<Invoice>) => Promise<void>;
  addCustomer: (data: NewCustomerData) => Promise<void>;
  addAdmin: (data: NewCustomerData) => Promise<void>;
  createCustomerOrgMember: (data: NewCustomerData, role?: UserRole.CUSTOMER | UserRole.CUSTOMER_MANAGER | UserRole.CUSTOMER_BRANCH_ADMIN) => Promise<void>;
  addBranch: (data: NewCustomerBranchData) => Promise<void>;
  updateBranch: (id: number, data: Partial<NewCustomerBranchData>) => Promise<void>;
  deleteBranch: (id: number) => Promise<void>;
  updateBranchProductPrice: (branchId: number, productId: number, variationId: string | undefined, price: number | null) => Promise<void>;
  updateCustomer: (id: number | string, data: Partial<NewCustomerData>) => Promise<void>;
  updateCustomerRole: (id: number | string, role: UserRole) => Promise<void>;
  deleteCustomer: (id: number | string) => Promise<void>;
  approveCustomer: (id: number | string) => Promise<void>;
  rejectCustomer: (id: number | string) => Promise<void>;
  addTestimonial: (data: NewTestimonialData) => Promise<void>;
  submitInquiry: (data: NewInquiryData) => Promise<void>;
  updateInquiryStatus: (id: number, status: 'Read' | 'Replied') => Promise<void>;
  updateSiteContent: (key: string, value: any) => Promise<void>;
  addNotification: (message: string, type: NotificationType) => void;
  setThemeMode: (mode: ThemeMode) => void;
  cycleThemeMode: () => void;
  removeNotification: (id: number) => void;
  markInboxNotificationRead: (id: number) => Promise<void>;
  markAllInboxNotificationsRead: () => Promise<void>;
  markAdminNotificationRead: (id: number) => Promise<void>;
  markAllAdminNotificationsRead: () => Promise<void>;
  resetDatabase: () => Promise<void>;
  setupRequired: boolean;
  isSetupMode: boolean;
  openSetup: () => void;
  closeSetup: () => void;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);

export type AppProviderProps = { children: ReactNode };
